import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Typography, Divider } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HistoryIcon from '@mui/icons-material/History';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import BarChartIcon from '@mui/icons-material/BarChart';
import WebhookIcon from '@mui/icons-material/Webhook';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SettingsIcon from '@mui/icons-material/Settings';
import ShieldIcon from '@mui/icons-material/Shield';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';

export const SIDEBAR_WIDTH = 240;

const groups = [
  {
    titleKey: 'sidebar.main',
    items: [
      { to: '/dashboard',      labelKey: 'sidebar.dashboard',  icon: <DashboardIcon fontSize="small" /> },
      { to: '/history',        labelKey: 'sidebar.history',    icon: <HistoryIcon fontSize="small" /> },
      { to: '/validate-batch', labelKey: 'sidebar.batch',      icon: <UploadFileIcon fontSize="small" /> },
      { to: '/stats',          labelKey: 'sidebar.stats',      icon: <BarChartIcon fontSize="small" /> },
    ],
  },
  {
    titleKey: 'sidebar.developer',
    items: [
      { to: '/webhooks',    labelKey: 'sidebar.webhooks',   icon: <WebhookIcon fontSize="small" /> },
      { to: '/email-lists', labelKey: 'sidebar.emailLists', icon: <ListAltIcon fontSize="small" /> },
    ],
  },
  {
    titleKey: 'sidebar.account',
    items: [
      { to: '/billing',  labelKey: 'sidebar.billing',  icon: <CreditCardIcon fontSize="small" /> },
      { to: '/settings', labelKey: 'sidebar.settings', icon: <SettingsIcon fontSize="small" /> },
    ],
  },
];

const adminGroup = {
  titleKey: 'sidebar.admin',
  items: [
    { to: '/admin', labelKey: 'sidebar.adminPanel', icon: <ShieldIcon fontSize="small" /> },
  ],
};

export const Sidebar: React.FC<{ isAdmin?: boolean }> = ({ isAdmin = false }) => {
  const location = useLocation();
  const { t } = useTranslation();

  const renderGroup = (group: typeof groups[0]) => (
    <Box key={group.titleKey} sx={{ mb: 2 }}>
      <Typography
        variant="caption"
        sx={{
          px: 2, py: 1, display: 'block',
          color: tokens.color.textMuted,
          textTransform: 'uppercase',
          fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em',
        }}
      >
        {t(group.titleKey)}
      </Typography>
      <List dense disablePadding>
        {group.items.map(item => {
          const active = location.pathname === item.to;
          return (
            <ListItemButton
              key={item.to}
              component={RouterLink}
              to={item.to}
              sx={{
                mx: 1, borderRadius: 1, py: 0.75,
                color: active ? tokens.color.accent : tokens.color.textSecondary,
                backgroundColor: active ? tokens.color.accentMuted : 'transparent',
                '&:hover': {
                  backgroundColor: active ? tokens.color.accentMuted : tokens.color.surface2,
                  color: active ? tokens.color.accent : tokens.color.textPrimary,
                },
                '& .MuiListItemIcon-root': { minWidth: 32, color: 'inherit' },
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText
                primary={t(item.labelKey)}
                primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 600 : 500 }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        borderRight: `1px solid ${tokens.color.border}`,
        backgroundColor: tokens.color.surface,
        minHeight: 'calc(100vh - 56px)',
        py: 2,
        display: { xs: 'none', md: 'block' },
      }}
    >
      {groups.map(renderGroup)}
      {isAdmin && (
        <>
          <Divider sx={{ my: 1 }} />
          {renderGroup(adminGroup)}
        </>
      )}
    </Box>
  );
};
